import { getDialogueById } from '../../data/dialogues';
import { SCENE_KEYS } from '../../data/gameConstants';
import { Collectible } from '../../objects/Collectible';
import { SaveSystem } from '../../systems/SaveSystem';
import { ZoneCoreSystems } from '../../systems/ZoneCoreSystems';
import { EmergencyBagOverlay } from '../../ui/EmergencyBagOverlay';
import { ObjectiveHint } from '../../ui/ObjectiveHint';
import { SceneTransition } from '../../utils/SceneTransition';
import { ZONE3_POSITIONS, Zone3Map } from './Zone3Map';
import { ZONE4_POSITIONS } from '../zone4/Zone4Map';

export class Zone3QuestFlow {
  private core: ZoneCoreSystems;
  private map: Zone3Map;
  private keris: Collectible;
  private hint: ObjectiveHint;
  private bagOverlay: EmergencyBagOverlay;
  private bagOpened = false;
  private transitioning = false;
  private exitX = ZONE3_POSITIONS.EXIT.x - 40;

  constructor(
    core: ZoneCoreSystems,
    map: Zone3Map,
    keris: Collectible,
    hint: ObjectiveHint,
    bagOverlay: EmergencyBagOverlay,
  ) {
    this.core = core;
    this.map = map;
    this.keris = keris;
    this.hint = hint;
    this.bagOverlay = bagOverlay;
  }

  start(): void {
    const arriveDone = this.core.quest.getStatus('zone3_arrive') === 'completed';
    let kerisDone = this.core.quest.getStatus('zone3_find_keris2') === 'completed';
    const bagDone = this.core.quest.getStatus('zone3_emergency_bag') === 'completed';

    this.keris.setUnlocked(false);

    let resumeKerisDialogue = false;
    if (this.keris.isCollected() && !kerisDone) {
      this.core.quest.completeQuest('zone3_find_keris2');
      kerisDone = true;
      resumeKerisDialogue = !bagDone;
    }

    if (bagDone) {
      this.openExit();
    }

    if (kerisDone) {
      this.keris.setUnlocked(false);
    } else if (arriveDone) {
      this.unlockKeris();
    }

    if (!arriveDone) {
      this.core.quest.activateQuest('zone3_arrive');
      this.core.showIntroDialogue('zone3_narrator', () => this.onArriveComplete());
    } else if (!kerisDone) {
      this.hint.show('Temukan Keris 2', ZONE3_POSITIONS.KERIS.x, ZONE3_POSITIONS.KERIS.y - 60);
    } else if (!bagDone) {
      if (resumeKerisDialogue) {
        this.startKerisRead();
      } else {
        this.hint.show(
          'Siapkan Tas Darurat',
          ZONE3_POSITIONS.BAG_STATION.x,
          ZONE3_POSITIONS.BAG_STATION.y - 50,
        );
      }
    } else {
      this.hint.show('Menuju Persimpangan', ZONE3_POSITIONS.EXIT.x - 70, ZONE3_POSITIONS.EXIT.y - 60);
    }
  }

  onKerisCollected(): void {
    this.core.quest.completeQuest('zone3_find_keris2');
    this.hint.hide();
    this.startKerisRead();
  }

  onSignInteract(): void {
    const board = getDialogueById('zone3_question_board');
    if (board) this.core.dialogue.show(board);
  }

  onBagStationInteract(): void {
    if (this.keris.isCollected() && this.core.quest.getStatus('zone3_find_keris2') !== 'completed') {
      this.core.quest.completeQuest('zone3_find_keris2');
    }
    if (this.core.quest.getStatus('zone3_find_keris2') !== 'completed') return;
    if (this.core.quest.getStatus('zone3_emergency_bag') === 'completed') return;

    const board = getDialogueById('zone3_question_board');
    const openBag = () => this.openBagMinigame();

    if (!this.bagOpened && board) {
      this.bagOpened = true;
      this.core.dialogue.show(board, openBag);
    } else {
      openBag();
    }
  }

  update(playerX: number, _playerY: number): void {
    this.checkExit(playerX);
  }

  private onArriveComplete(): void {
    this.core.quest.completeQuest('zone3_arrive');
    this.unlockKeris();
  }

  private unlockKeris(): void {
    this.keris.setUnlocked(true);
    this.core.quest.activateQuest('zone3_find_keris2');
    this.hint.show('Temukan Keris 2', ZONE3_POSITIONS.KERIS.x, ZONE3_POSITIONS.KERIS.y - 60);
  }

  private startKerisRead(): void {
    if (this.core.quest.getStatus('zone3_find_keris2') !== 'completed') {
      this.core.quest.completeQuest('zone3_find_keris2');
    }

    const kerisDialogue = getDialogueById('zone3_keris2');
    const localDialogue = getDialogueById('zone3_keris2_local');

    const showBagStage = () => {
      this.core.quest.activateQuest('zone3_emergency_bag');
      this.hint.show(
        'Siapkan Tas Darurat',
        ZONE3_POSITIONS.BAG_STATION.x,
        ZONE3_POSITIONS.BAG_STATION.y - 50,
      );
    };

    const showLocal = () => {
      if (localDialogue) {
        this.core.dialogue.show(localDialogue, showBagStage);
      } else {
        showBagStage();
      }
    };

    if (kerisDialogue) {
      this.core.dialogue.show(kerisDialogue, showLocal);
    } else {
      showLocal();
    }
  }

  private openBagMinigame(): void {
    if (this.bagOverlay.isVisible()) return;

    this.bagOverlay.configure({
      onVisibility: (visible) => {
        this.core.setHudVisible(!visible);
        this.core.setMobileControlsVisible(!visible);
      },
      onComplete: (result) => {
        SaveSystem.save({ emergencyBagPoints: result.totalPoints });
        if (result.totalPoints > 0) {
          this.core.score.addScore(result.totalPoints, 'Tas Darurat BNPB', true);
        }
        this.onBagComplete();
      },
    });

    this.bagOverlay.show(this.core.scene);
  }

  private onBagComplete(): void {
    this.core.quest.completeQuest('zone3_emergency_bag');
    this.hint.hide();
    this.openExit();
  }

  private openExit(): void {
    this.map.openExitPath();
    this.core.removeObstacleNear(ZONE3_POSITIONS.EXIT_BLOCK.x, ZONE3_POSITIONS.EXIT_BLOCK.y);
    this.core.quest.activateQuest('zone3_to_zone4');
    this.hint.show('Menuju Persimpangan', ZONE3_POSITIONS.EXIT.x - 70, ZONE3_POSITIONS.EXIT.y - 60);
    SaveSystem.save({ rafflesiaPoints: this.core.score.getScore() });
  }

  private checkExit(playerX: number): void {
    if (this.transitioning) return;
    if (this.core.quest.getStatus('zone3_emergency_bag') !== 'completed') return;
    if (playerX >= this.exitX) {
      this.transitionToZone4();
    }
  }

  private transitionToZone4(): void {
    this.transitioning = true;
    this.core.quest.completeQuest('zone3_to_zone4');
    this.core.quest.activateQuest('zone4_find_keris3');

    SaveSystem.save({
      currentScene: SCENE_KEYS.ZONE4,
      currentZone: 4,
      rafflesiaPoints: this.core.score.getScore(),
      playerX: ZONE4_POSITIONS.SPAWN.x,
      playerY: ZONE4_POSITIONS.SPAWN.y,
    });

    this.hint.hide();
    SceneTransition.fadeOutAndStart(this.core.scene, SCENE_KEYS.ZONE4, 400);
  }

  destroy(): void {
    this.bagOverlay.destroy();
    this.hint.destroy();
  }
}
